import { Link } from 'react-router-dom';
import type { AdminPayment } from '@/lib/api';

/**
 * PaymentsTable — shared render used by both the global
 * /dashboard/payments page and the Payments section on the
 * /dashboard/users/:id detail page.
 *
 * Read-only — refunds don't happen here: RevenueCat refunds are issued
 * in the App Store / Play Store consoles, and the RC webhook
 * reconciles them into payments.refunded_amount.
 *
 * showUser=true renders the email column (used on the global page);
 * the per-user view hides it since every row is the same user.
 */
export function PaymentsTable({
  payments,
  showUser,
}: {
  payments: AdminPayment[];
  showUser: boolean;
}) {
  if (payments.length === 0) {
    return <div style={{ color: 'var(--ink-3)', padding: 24, textAlign: 'center', fontSize: 13 }}>No payments on record.</div>;
  }
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, overflowX: 'auto' }}>
      <table style={tbl}>
        <thead>
          <tr>
            <th style={th}>When</th>
            <th style={th}>Vendor</th>
            {showUser && <th style={th}>User</th>}
            <th style={th}>Charge id</th>
            <th style={th}>Amount</th>
            <th style={th}>Refunded</th>
            <th style={th}>Status</th>
            <th style={{ ...th, textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <PaymentRow key={p.id} payment={p} showUser={showUser} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentRow({
  payment,
  showUser,
}: {
  payment: AdminPayment;
  showUser: boolean;
}) {
  return (
    <tr>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {new Date(payment.created_at).toLocaleString(undefined, {
          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        })}
      </td>
      <td style={td}>
        <span className="mono" style={vendorChip(payment.vendor)}>
          {payment.vendor}
        </span>
      </td>
      {showUser && (
        <td style={td}>
          {payment.user_id ? (
            <Link
              to={`/dashboard/users/${payment.user_id}`}
              style={{ color: 'var(--ink)', textDecoration: 'none', borderBottom: '1px dashed var(--line-strong)' }}
            >
              {payment.user_email || payment.user_id.slice(0, 8)}
            </Link>
          ) : (
            <span style={{ color: 'var(--ink-3)' }}>—</span>
          )}
        </td>
      )}
      <td style={td}>
        <span className="mono" style={{ fontSize: 11 }}>{payment.vendor_payment_id}</span>
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatMoney(payment.amount, payment.currency)}</td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {payment.refunded_amount > 0
          ? <span style={{ color: '#a86b00' }}>{formatMoney(payment.refunded_amount, payment.currency)}</span>
          : <span style={{ color: 'var(--ink-3)' }}>—</span>}
      </td>
      <td style={td}><StatusChip status={payment.status} /></td>
      <td style={{ ...td, textAlign: 'right' }}>
        {payment.receipt_url ? (
          <a
            href={payment.receipt_url}
            target="_blank"
            rel="noopener"
            style={ghostBtn}
            title="Open vendor receipt"
          >
            Receipt ↗
          </a>
        ) : (
          <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
        )}
      </td>
    </tr>
  );
}

function StatusChip({ status }: { status: AdminPayment['status'] }) {
  let bg = 'var(--chip)';
  let fg = 'var(--ink-2)';
  switch (status) {
    case 'succeeded':           bg = 'rgba(46,125,50,0.10)'; fg = '#2e7d32'; break;
    case 'partially_refunded':  bg = 'rgba(168,107,0,0.12)'; fg = '#a86b00'; break;
    case 'refunded':            bg = 'rgba(168,51,31,0.12)'; fg = '#a8331f'; break;
    case 'failed':              bg = 'rgba(168,51,31,0.18)'; fg = '#a8331f'; break;
  }
  return (
    <span
      className="mono"
      style={{
        background: bg, color: fg,
        fontSize: 10, letterSpacing: 1,
        padding: '3px 8px', borderRadius: 100,
        textTransform: 'uppercase', fontWeight: 600,
      }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function vendorChip(vendor: string): React.CSSProperties {
  return {
    background: vendor === 'revenuecat' ? 'rgba(99,91,255,0.10)' : 'rgba(43,29,20,0.08)',
    color: vendor === 'revenuecat' ? '#5b53d4' : 'var(--ink-2)',
    fontSize: 10,
    letterSpacing: 1,
    padding: '3px 8px',
    borderRadius: 100,
    textTransform: 'uppercase',
    fontWeight: 700,
  };
}

function formatMoney(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(minor / 100);
  } catch {
    return `${currency.toUpperCase()} ${(minor / 100).toFixed(2)}`;
  }
}

const tbl: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--line-strong)',
  fontWeight: 600,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const td: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--line)',
  color: 'var(--ink)',
  verticalAlign: 'middle',
};
const ghostBtn: React.CSSProperties = {
  display: 'inline-block',
  background: 'var(--bg)',
  color: 'var(--ink-2)',
  border: '1px solid var(--line-strong)',
  borderRadius: 100,
  padding: '5px 11px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: 'var(--font-ui)',
  whiteSpace: 'nowrap',
};
